/**
 * Componente de Distribuição por Action PGMQ
 * 
 * Exibe gráfico de pizza com distribuição de mensagens por tipo de action
 * (start_flow, process_response, continue_after_delay, retry_node).
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { useFlowQueueByActionPgmq } from '@/hooks/useFlowQueueMonitorPgmq';

// Cores para cada tipo de action
const ACTION_COLORS: Record<string, string> = {
  start_flow: 'hsl(var(--chart-1))',
  process_response: 'hsl(var(--chart-2))',
  continue_after_delay: 'hsl(var(--chart-3))',
  retry_node: 'hsl(var(--chart-4))',
};

// Labels amigáveis para cada action
const ACTION_LABELS: Record<string, string> = {
  start_flow: 'Iniciar Flow',
  process_response: 'Processar Resposta',
  continue_after_delay: 'Continuar após Delay',
  retry_node: 'Retry de Nó',
};

// Configuração do gráfico
const chartConfig = {
  start_flow: { label: 'Iniciar Flow', color: 'hsl(var(--chart-1))' },
  process_response: { label: 'Processar Resposta', color: 'hsl(var(--chart-2))' },
  continue_after_delay: { label: 'Continuar após Delay', color: 'hsl(var(--chart-3))' },
  retry_node: { label: 'Retry de Nó', color: 'hsl(var(--chart-4))' },
};

export function PgmqActionDistribution() {
  const { data: actionData, isLoading } = useFlowQueueByActionPgmq();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribuição por Action</CardTitle>
          <CardDescription>Tipos de operação na fila</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Formatar dados para o gráfico
  const chartData = (actionData || [])
    .filter(item => item.count > 0)
    .map((item) => ({
      name: ACTION_LABELS[item.action] || item.action,
      value: item.count,
      action: item.action,
    }));

  const totalMessages = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Distribuição por Action</CardTitle>
        <CardDescription>
          Total: {totalMessages} mensagens nas últimas 24h
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={ACTION_COLORS[entry.action] || `hsl(var(--chart-${(index % 5) + 1}))`}
                    />
                  ))}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
